import { Box, Divider, Grid, SxProps, Typography } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { PayslipDetailed } from "./payslipAPI";
import { Fragment } from "react";
import formatCurrency from "../../../common/helpers/formatCurrency";
import formatNumber from "../../../common/helpers/formatNumber";
import { useAppSelector } from "../../../app/hooks";
import { HEALTH_INSURANCE_PER_DIEM_RATE } from "../constants";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";

type Props = {
  payslip: PayslipDetailed;
  onClose: () => void;
};

export const GridRow = ({
  title,
  value,
  sx,
}: {
  title: string;
  value?: string;
  sx?: SxProps;
}) => {
  return (
    <>
      <Grid item xs={7} sx={{ fontSize: 14, ...sx }}>
        {title}
      </Grid>
      <Grid item xs={5} sx={{ fontSize: 14, ...sx, textAlign: "right" }}>
        {value}
      </Grid>
    </>
  );
};

export const GridRowThirds = ({
  title,
  value1,
  value2,
  sx,
}: {
  title: string;
  value1: string;
  value2: string;
  sx?: SxProps;
}) => {
  return (
    <>
      <Grid item xs={4} sx={{ ...sx, fontSize: 14 }}>
        {title}
      </Grid>
      <Grid item xs={4} sx={{ ...sx, textAlign: "right", fontSize: 14 }}>
        {value1}
      </Grid>
      <Grid item xs={4} sx={{ ...sx, textAlign: "right", fontSize: 14 }}>
        {value2}
      </Grid>
    </>
  );
};

export const PayslipDetailsContent = ({ payslip, onClose }: Props) => {
  const { t } = useTranslation();
  const localizedExpenseTypes = useAppSelector(
    (state) => state.localized?.expenseType,
  );

  const payslipHealthInsuranceRate = Number(
    payslip?.employeeHealthInsuranceRate || HEALTH_INSURANCE_PER_DIEM_RATE,
  ).toLocaleString("fi");

  return (
    <DialogContentWrapper
      title={t("payslips:payslip")}
      onCancel={onClose}
      customCancelButtonLabel={t("common:close")}
    >
      <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          sx={{
            height: 34,
            width: 34,
          }}
          alt="Eezy logo"
          src="/assets/eezy_round_logo.svg"
        />
      </Grid>
      <Grid item xs={10}>
        <Typography variant="header" fontSize={18} fontWeight={700}>
          {`${t("schedulingPeriods:schedulingPeriod")} ${format(
            new Date(payslip.periodStart),
            leadingZerosDateFormat,
          )} - ${format(new Date(payslip.periodEnd), leadingZerosDateFormat)}`}
        </Typography>
        <Typography fontSize={14}>{`${t("payslips:receiptNumber")} ${
          payslip.payslipNumber
        }`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <GridRow
          title={t("payslips:salaryDate")}
          value={format(new Date(payslip.salaryDate), leadingZerosDateFormat)}
          sx={{ fontSize: 16 }}
        />
        <GridRow
          title={t("payslips:payer")}
          value={payslip.payer?.name}
          sx={{ fontSize: 16 }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <GridRow
          title={t("payslips:recipient")}
          value={payslip.recipient?.name}
          sx={{ fontSize: 16 }}
        />
        <GridRow
          title={t("payslips:ssn")}
          value={payslip.recipient?.ssn}
          sx={{ fontSize: 16 }}
        />
        <GridRow
          title={t("payslips:bank")}
          value={payslip.recipient?.iban}
          sx={{ fontSize: 16 }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <Grid item xs={12}>
          <Typography variant="header" fontSize={18} fontWeight={600}>
            {t("payslips:taxDeductionCardAndInfo")}
          </Typography>
        </Grid>
        <Grid item xs={12} pb={2}>
          <Typography>
            {t("payslips:accumulationFromAllEezyCompanies")}
          </Typography>
        </Grid>
        <GridRow
          title={t("taxDeductionCard:basicWithholdingRate")}
          value={formatNumber(payslip.recipient.withholdingRate)}
        />
        <GridRow
          title={t("taxDeductionCard:additionalWithholdingRate")}
          value={formatNumber(payslip.recipient.additionalWithholdingRate)}
        />
        <GridRow
          title={t("taxDeductionCard:incomeCeilingAnnual")}
          value={formatCurrency(payslip.recipient.incomeCeiling)}
        />
        <GridRow
          title={t("taxDeductionCard:incomeFromTaxPeriodBeginning")}
          value={formatCurrency(payslip.recipient.taxable)}
        />
        <GridRow
          title={t("taxDeductionCard:withholdingFromTaxPeriodBeginning")}
          value={formatCurrency(payslip.recipient.taxWithholding)}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={12}>
          <Typography variant="header" fontSize={18} fontWeight={600} pb={1}>
            {t("talentProfile:salary")}
          </Typography>
        </Grid>
        {payslip.rows.map((row, i) => (
          <Fragment key={i}>
            {row.title ? (
              <Grid
                item
                xs={12}
                sx={{
                  backgroundColor: "#F1EDF7",
                  textTransform: "uppercase",
                  mb: "8px",
                  p: "4px 8px",
                }}
                pb={0.5}
              >
                <Typography
                  pl="8px"
                  fontSize={14}
                  fontWeight={700}
                  variant="header"
                >
                  {row.title}
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Divider sx={{ marginY: 1.5 }} />
              </Grid>
            )}
            {row.rows.map((item, i) => (
              <Fragment key={i}>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {localizedExpenseTypes[item.expenseTypeCode]}
                  </Typography>
                </Grid>
                <Grid item xs={5} pb={2}>
                  <Typography sx={{ fontSize: 14 }}>
                    {item.description}
                  </Typography>
                </Grid>
                <Grid item xs={7} textAlign="right">
                  <Typography>{formatCurrency(item.expense)}</Typography>
                </Grid>
              </Fragment>
            ))}
          </Fragment>
        ))}
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <GridRow
          title={t("payslips:taxWithholding")}
          value={formatCurrency(payslip.subtotalsPeriod.taxWithholding)}
        />
        <GridRow
          title={t("payslips:pensionContribution")}
          value={formatCurrency(payslip.subtotalsPeriod.pensionInsurance)}
        />
        <GridRow
          title={t("payslips:unemploymentInsuranceLong")}
          value={formatCurrency(payslip.subtotalsPeriod.unemploymentInsurance)}
        />
        <GridRow
          title={`${t(
            "payslips:sickBenefit",
          )} (${payslipHealthInsuranceRate} %)`}
          value={formatCurrency(payslip.subtotalsPeriod.healthInsurancePerDiem)}
        />
        <GridRow
          title={t("payslips:taxExemptLong")}
          value={formatCurrency(payslip.subtotalsPeriod.taxExempt)}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <GridRow
          title={t("payslips:grossSalary")}
          value={formatCurrency(payslip.subtotalsPeriod.incomeGross)}
        />
        <GridRow
          title={t("talentProfile:totalPayment")}
          value={formatCurrency(payslip.subtotalsPeriod.incomeNet)}
          sx={{ fontSize: 16, fontWeight: 600, pt: 1 }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <Grid item xs={12} pb={1}>
          <Typography variant="header" fontSize={18} fontWeight={700}>
            {t("payslips:salaryCertificate")}
          </Typography>
        </Grid>
        <GridRowThirds
          title={payslip.payer?.name}
          value1={t("payslips:period")}
          value2={t("payslips:presentYear")}
          sx={{ fontWeight: 600 }}
        />
        <GridRowThirds
          title={t("payslips:incomeGross")}
          value1={formatCurrency(payslip.subtotalsPeriod.incomeGross)}
          value2={formatCurrency(payslip.subtotalsYear.incomeGross)}
        />
        <GridRowThirds
          title={t("payslips:fringeBenefitsLong")}
          value1={formatCurrency(payslip.subtotalsPeriod.fringeBenefits)}
          value2={formatCurrency(payslip.subtotalsYear.fringeBenefits)}
        />
        <GridRowThirds
          title={t("payslips:taxableLong")}
          value1={formatCurrency(payslip.subtotalsPeriod.taxable)}
          value2={formatCurrency(payslip.subtotalsYear.taxable)}
        />
        <GridRowThirds
          title={t("payslips:taxExempt")}
          value1={formatCurrency(payslip.subtotalsPeriod.taxExempt)}
          value2={formatCurrency(payslip.subtotalsYear.taxExempt)}
        />
        <GridRowThirds
          title={t("payslips:taxWithholding")}
          value1={formatCurrency(payslip.subtotalsPeriod.taxWithholding)}
          value2={formatCurrency(payslip.subtotalsYear.taxWithholding)}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginTop: 1.5 }} />
      </Grid>
    </DialogContentWrapper>
  );
};
